import { useEffect, useMemo, useRef, useState } from 'react';
import { MessageSquare, MoreVertical, UserCircle, ChevronDown, Bot, Zap, Power, Check, Circle, Clock, AlertCircle } from 'lucide-react';
import Avatar from '../ui/Avatar';
import MessageComposer from './MessageComposer';
import { CONVERSATION_STATUS, CONVERSATION_STATUS_LABELS } from '../../data/conversations';

const STATUS_STYLES = {
  [CONVERSATION_STATUS.ATIVO]: 'bg-emerald-500 text-white border-emerald-600 shadow-emerald-200',
  [CONVERSATION_STATUS.AGUARDANDO]: 'bg-amber-400 text-amber-900 border-amber-500 shadow-amber-200',
  [CONVERSATION_STATUS.FINALIZADO]: 'bg-slate-400 text-white border-slate-500 shadow-slate-200',
};

export default function ChatWindow({ conversation, currentUser, onSendMessage, aiAgents, toggleAi, onAssignAi, onInfoClick, onUpdateConversation }) {
  const [isStatusMenuOpen, setIsStatusMenuOpen] = useState(false);
  const [isAiMenuOpen, setIsAiMenuOpen] = useState(false);
  const [isMoreMenuOpen, setIsMoreMenuOpen] = useState(false);
  const scrollRef = useRef(null);

  const currentStatus = conversation?.conversationStatus ?? CONVERSATION_STATUS.ATIVO;
  const statusButtonStyle = STATUS_STYLES[currentStatus] ?? STATUS_STYLES[CONVERSATION_STATUS.ATIVO];

  const currentAi = useMemo(() => 
    aiAgents.find(a => a.id === conversation?.assignedAiId) || 
    aiAgents.find(a => a.teamId === conversation?.teamId)
  , [aiAgents, conversation]);

  const getSenderLabel = (msg) => {
    if (!msg) return '';
    if (msg.isAi) return msg.aiName ? `IA • ${msg.aiName}` : 'IA';
    if (msg.senderName || msg.sender_name) return msg.senderName || msg.sender_name;
    if (msg.user?.name) return msg.user.name;
    if (msg.sender === 'agent') return 'Agente';
    if (msg.sender === 'customer') return conversation?.name || 'Cliente';
    return 'Sistema';
  };

  const humanHeaderLabel = currentUser?.name ? `Atendimento Humano • ${currentUser.name}` : 'Atendimento Humano';

  useEffect(() => { scrollRef.current?.scrollIntoView({ behavior: "smooth" }); }, [conversation?.messages]);

  if (!conversation) return (
    <div className="flex-1 flex flex-col items-center justify-center bg-appSurfaceBorder/10 text-appTextMuted">
      <div className="bg-appSurface p-12 rounded-full shadow-sm mb-6 animate-pulse">
        <MessageSquare size={64} className="text-appTextMuted" />
      </div>
      <h2 className="text-xl font-medium text-appText">Selecione uma conversa</h2>
      <p className="text-sm">Clique em um contato ao lado para iniciar</p>
    </div>
  );

  return (
    <div className="flex-1 flex flex-col h-full bg-appPage overflow-hidden relative">
      <div className="h-[72px] bg-appSurface border-b border-appSurfaceBorder flex items-center justify-between px-6 z-10 shrink-0">
        <div className="flex items-center gap-2 min-w-0 shrink-0">
          <div className="flex items-center gap-3 cursor-pointer min-w-0 shrink-0" onClick={onInfoClick}>
            <Avatar src={conversation.avatarUrl} label={conversation.avatarLabel ?? conversation.avatar} />
            <div className="text-left min-w-0">
              <span className="font-bold text-appText block leading-tight truncate">{conversation.name}</span>
              <span className="text-[11px] text-appTextMuted flex items-center gap-1">
                {conversation.aiActive ? <><Zap size={10} className="text-appPrimary" /> Respondido por {currentAi?.publicName}</> : humanHeaderLabel}
              </span>
            </div>
          </div>
          <div className="relative shrink-0">
            <button
              type="button"
              onClick={() => { setIsAiMenuOpen(false); setIsStatusMenuOpen(false); setIsMoreMenuOpen((v) => !v); }}
              className="p-2 text-appTextMuted hover:bg-appSurfaceBorder/30 rounded-full transition-colors"
              aria-label="Mais opções"
            >
              <MoreVertical size={22} />
            </button>
            {isMoreMenuOpen && (
              <>
                <div className="fixed inset-0 z-40" aria-hidden onClick={() => setIsMoreMenuOpen(false)} />
                <div className="absolute left-0 top-full mt-1 z-50 w-56 bg-appSurface rounded-2xl shadow-2xl border border-appSurfaceBorder py-2">
                  <div className="px-4 py-2 border-b border-appSurfaceBorder mb-2">
                    <p className="text-[10px] font-bold text-appTextMuted uppercase tracking-widest">Mais opções</p>
                  </div>
                  <button type="button" onClick={() => { onInfoClick(); setIsMoreMenuOpen(false); }} className="w-full flex items-center gap-3 px-4 py-3 hover:bg-appSurfaceBorder/20 text-left text-sm font-medium text-appText">
                    <UserCircle size={18} className="text-appTextMuted" />
                    Abrir perfil do contato
                  </button>
                </div>
              </>
            )}
          </div>
        </div>

        <div className="flex items-center gap-4 shrink-0">
          {onUpdateConversation && (
            <div className="relative">
              <button
                type="button"
                onClick={() => { setIsAiMenuOpen(false); setIsMoreMenuOpen(false); setIsStatusMenuOpen(!isStatusMenuOpen); }}
                className={`flex items-center gap-2 px-4 py-2 rounded-xl text-[10px] font-bold transition-all border shadow-sm h-[36px] ${statusButtonStyle}`}
              >
                <Circle size={10} className="opacity-90" fill="currentColor" />
                {CONVERSATION_STATUS_LABELS[currentStatus]?.toUpperCase() ?? 'ATIVO'}
                <ChevronDown size={12} className="opacity-70" />
              </button>
              {isStatusMenuOpen && (
                <>
                  <div className="fixed inset-0 z-40" aria-hidden onClick={() => setIsStatusMenuOpen(false)} />
                  <div className="absolute top-12 right-0 w-56 bg-appSurface rounded-2xl shadow-2xl border border-appSurfaceBorder py-2 z-50 animate-in zoom-in-95 duration-150">
                    <div className="px-4 py-2 border-b border-appSurfaceBorder mb-2">
                      <p className="text-[10px] font-bold text-appTextMuted uppercase tracking-widest">Status da conversa</p>
                    </div>
                    {[CONVERSATION_STATUS.ATIVO, CONVERSATION_STATUS.AGUARDANDO, CONVERSATION_STATUS.FINALIZADO].map((value) => (
                      <button
                        key={value}
                        type="button"
                        onClick={() => {
                          onUpdateConversation(conversation.id, { conversationStatus: value });
                          setIsStatusMenuOpen(false);
                        }}
                        className={`w-full flex items-center gap-3 px-4 py-3 hover:bg-appSurfaceBorder/20 text-left text-sm font-medium transition-colors ${
                          currentStatus === value ? 'bg-appSurfaceBorder/20' : ''
                        }`}
                      >
                        <span className={`w-2.5 h-2.5 rounded-full shrink-0 ${
                          value === CONVERSATION_STATUS.ATIVO ? 'bg-emerald-500' :
                          value === CONVERSATION_STATUS.AGUARDANDO ? 'bg-amber-400' : 'bg-slate-400'
                        }`} />
                        <span className={currentStatus === value ? 'font-bold text-appText' : 'text-appText'}>{CONVERSATION_STATUS_LABELS[value]}</span>
                      </button>
                    ))}
                  </div>
                </>
              )}
            </div>
          )}
          <div className="relative">
            <button 
              type="button"
              onClick={() => { setIsStatusMenuOpen(false); setIsMoreMenuOpen(false); setIsAiMenuOpen(!isAiMenuOpen); }}
              className={`flex items-center gap-2 px-4 py-2 rounded-xl text-[10px] font-bold transition-all border shadow-sm h-[36px] ${
                conversation.aiActive ? 'bg-appPrimary text-white border-appPrimary' : 'bg-appSurface text-appTextMuted border-appSurfaceBorder hover:bg-appSurfaceBorder/30'
              }`}
            >
              <Bot size={14} />
              {conversation.aiActive ? 'AUTOMAÇÃO ATIVA' : 'MODO MANUAL'}
              <ChevronDown size={12} className={conversation.aiActive ? 'text-white/70' : 'text-appTextMuted'} />
            </button>

            {isAiMenuOpen && (
              <div className="absolute top-12 right-0 w-64 bg-appSurface rounded-2xl shadow-2xl border border-appSurfaceBorder py-2 z-50 animate-in zoom-in-95 duration-150">
                <div className="px-4 py-2 border-b border-appSurfaceBorder mb-2">
                  <p className="text-[10px] font-bold text-appTextMuted uppercase tracking-widest">Ações do Robô</p>
                </div>
                <button type="button" onClick={() => { toggleAi(conversation.id); setIsAiMenuOpen(false); }} className="w-full flex items-center gap-3 px-4 py-3 hover:bg-appSurfaceBorder/20 transition-colors">
                  <div className={`p-2 rounded-lg ${conversation.aiActive ? 'bg-red-50 text-red-500' : 'bg-appPrimaryLight text-appPrimary'}`}><Power size={16} /></div>
                  <div className="text-left"><p className="text-sm font-bold text-appText">{conversation.aiActive ? 'Desativar IA' : 'Ativar IA'}</p></div>
                </button>
                <div className="px-4 py-2 border-b border-appSurfaceBorder mt-2 mb-2"><p className="text-[10px] font-bold text-appTextMuted uppercase tracking-widest">Trocar Agente IA</p></div>
                {aiAgents.map(agent => (
                  <button type="button" key={agent.id} onClick={() => { onAssignAi(conversation.id, agent.id); setIsAiMenuOpen(false); }} className={`w-full flex items-center gap-3 px-4 py-2 hover:bg-appSurfaceBorder/20 ${conversation.assignedAiId === agent.id ? 'bg-appSurfaceBorder/20' : ''}`}>
                    <Avatar label={agent.publicName} size="sm" />
                    <div className="text-left"><p className="text-xs font-bold text-appText">{agent.publicName}</p></div>
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="flex-1 overflow-y-auto p-8 space-y-4">
        {conversation.messages.map((msg) => (
          <div key={msg.id} className={`flex ${msg.sender === 'agent' ? 'justify-end' : 'justify-start'} animate-in slide-in-from-bottom-2 duration-300`}>
            <div className={`max-w-[75%] px-4 py-2.5 rounded-2xl shadow-sm text-sm relative transition-all ${msg.sender === 'agent' ? 'bg-appSurface text-appText rounded-tr-none border border-appSurfaceBorder' : 'bg-appPrimary text-white rounded-tl-none'}`}>
              {getSenderLabel(msg) && (
                <div className={`flex items-center gap-1.5 mb-1 text-[10px] font-bold uppercase tracking-widest ${msg.sender === 'agent' ? 'text-appTextMuted' : 'text-white/80'}`}>
                  {msg.isAi && <Bot size={10} />}
                  {getSenderLabel(msg)}
                </div>
              )}
              <p className="whitespace-pre-wrap leading-relaxed">{msg.text}</p>
              <div className={`flex items-center justify-end gap-1 mt-1 opacity-60 text-[10px] ${msg.sender === 'agent' ? 'text-appTextMuted' : 'text-white/80'}`}>
                {msg.time}
                {msg.sender === 'agent' && msg.status === 'pending' && <Clock size={12} className="animate-pulse" title="Enviando para WhatsApp..." />}
                {msg.sender === 'agent' && msg.status === 'sent' && <Check size={12} className="text-appPrimary" title="Enviada" />}
                {msg.sender === 'agent' && msg.status === 'failed' && <AlertCircle size={12} className="text-amber-500" title="Falha ao enviar" />}
                {msg.sender === 'agent' && !msg.status && <Check size={12} className="text-appPrimary" />}
              </div>
            </div>
          </div>
        ))}
        <div ref={scrollRef} />
      </div>

      <MessageComposer
        conversationId={conversation.id}
        onSendMessage={onSendMessage}
        placeholder={conversation.aiActive ? 'Automação ativa... Digite para assumir o chat' : 'Envie uma mensagem...'}
      />
    </div>
  );
}
